import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'

import api from '@/client/core/api'
import { useSystemStore } from '@/client/store/useSystemStore'
import { TCampaign, TCampaignPrize } from '@/typings/api/publics/campaigns.type'
import {
  TGetTikTokCampaignAnalyticsParams as TParams,
  TTikTokCampaignAnalytic,
  TTikTokTrafficSource
} from '@/typings/api/publics/tiktok_campaign_analytics.type'

type TikTokAnalyticsPublicState = {
  campaignAnalytics: TTikTokCampaignAnalytic[]
  trafficSource: TTikTokTrafficSource[]
  campaign: TCampaign | null
  prizes: TCampaignPrize[]
  params: TParams
  error: boolean
  loading: boolean
}

const createInitialState = (): TikTokAnalyticsPublicState => ({
  campaignAnalytics: [],
  trafficSource: [],
  campaign: null,
  prizes: [],
  params: {
    hash: '',
    prizeId: '',
    startDatetime: '',
    endDatetime: ''
  },
  error: false,
  loading: false
})

export const useTikTokAnalyticsPublicStore = defineStore('tiktokAnalyticsPublicStore', () => {
  const systemStore = useSystemStore()
  const initialState = createInitialState()

  const campaignAnalytics = ref<TikTokAnalyticsPublicState['campaignAnalytics']>(initialState.campaignAnalytics)
  const trafficSource = ref<TikTokAnalyticsPublicState['trafficSource']>(initialState.trafficSource)
  const campaign = ref<TikTokAnalyticsPublicState['campaign']>(initialState.campaign)
  const prizes = ref<TikTokAnalyticsPublicState['prizes']>(initialState.prizes)
  const params = ref<TikTokAnalyticsPublicState['params']>(initialState.params)
  const error = ref<TikTokAnalyticsPublicState['error']>(initialState.error)
  const loading = ref<TikTokAnalyticsPublicState['loading']>(initialState.loading)

  const reset = async (): Promise<void> => {
    const nextState = createInitialState()
    campaignAnalytics.value = nextState.campaignAnalytics
    trafficSource.value = nextState.trafficSource
    campaign.value = nextState.campaign
    prizes.value = nextState.prizes
    params.value = nextState.params
    error.value = nextState.error
    loading.value = nextState.loading
  }

  const fetchCampaignAnalytics = async (): Promise<void> => {
    error.value = false
    loading.value = true

    await systemStore.setLoading(true)

    const result = await api.get('/publics/tiktok_campaign_analytics', { params: params.value })

    loading.value = false

    await systemStore.setLoading(false)

    if (!result.data) {
      error.value = true
      return
    }

    campaignAnalytics.value = result.data.analytics
    trafficSource.value = result.data.impressions
  }

  const fetchCampaign = async (): Promise<void> => {
    error.value = false

    const result = await api.get('/publics/campaigns/' + params.value.hash)

    if (!result.data) {
      error.value = true
      return
    }

    campaign.value = result.data
  }

  const fetchPrizes = async (): Promise<void> => {
    error.value = false

    const result = await api.get(`/publics/campaigns/${params.value.hash}/prizes`)

    if (!result.data) {
      error.value = true
      return
    }

    prizes.value = result.data
  }

  const setParams = (newParams: Partial<TParams>): void => {
    params.value = { ...params.value, ...newParams }
  }

  return {
    campaignAnalytics: readonly(campaignAnalytics),
    trafficSource: readonly(trafficSource),
    campaign: readonly(campaign),
    prizes: readonly(prizes),
    params: readonly(params),
    error: readonly(error),
    loading: readonly(loading),
    reset,
    fetchCampaignAnalytics,
    fetchCampaign,
    fetchPrizes,
    setParams
  }
})
